import type { ComponentProps } from 'react';
import {
    selectionOf,
    thumbsOf,
    VEHICLE_RANGE_FACETS,
    type VehicleFilterBounds,
    type VehicleFilters,
    type VehicleRange,
    type VehicleRangeFacet,
} from './vehicleFilters';
import { VEHICLE_MILEAGE_STEP, VEHICLE_PRICE_STEP } from './vehicleListing.constants';
import { rangeLabel, scaleLabel, thumbLabel } from './vehicleRangeLabels';
import { VehicleRangeFilter } from './VehicleRangeFilter';

type SliderProps = ComponentProps<typeof VehicleRangeFilter>;

type VehicleRangeFiltersProps = {
    filters: VehicleFilters;
    bounds: VehicleFilterBounds;
    onChange: (filters: VehicleFilters) => void;
};

const SLIDERS: Record<VehicleRangeFacet, { legend: string; step: number }> = {
    price: { legend: 'Preis', step: VEHICLE_PRICE_STEP },
    mileage: { legend: 'Kilometer', step: VEHICLE_MILEAGE_STEP },
    year: { legend: 'Erstzulassung', step: 1 },
};

/** Every scale runs upwards, lowest on the left — the year too. */
function sliderProps(
    facet: VehicleRangeFacet,
    range: VehicleRange,
    filters: VehicleFilters,
    onChange: VehicleRangeFiltersProps['onChange'],
) {
    const value = thumbsOf(filters[facet], range);

    return {
        ...SLIDERS[facet],
        valueLabel: rangeLabel(facet, value),
        bounds: range,
        value,
        onChange: (next) => onChange({ ...filters, [facet]: selectionOf(next, range) }),
        valueText: (thumb) => thumbLabel(facet, thumb),
        startLabel: scaleLabel(facet, range.min),
        endLabel: scaleLabel(facet, range.max),
    } satisfies SliderProps;
}

/** Price, mileage and first registration — each only where the stock spans a range. */
export function VehicleRangeFilters({ filters, bounds, onChange }: VehicleRangeFiltersProps) {
    return (
        <>
            {VEHICLE_RANGE_FACETS.map((facet) => {
                const range = bounds[facet];

                return range ? (
                    <VehicleRangeFilter key={facet} {...sliderProps(facet, range, filters, onChange)} />
                ) : null;
            })}
        </>
    );
}
